import { faPersonRunning } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Col, Row, Switch, Card, Modal, Button, Typography } from 'antd'
import React, { useEffect, useState, useRef } from 'react'
import MedicalHistoryFormTemplates from '../../utils/templates'
import DesktopMedicalHistoryNav from './Nav'
import useLifestyle from '../../hooks/useLifestyle'
import { LIFESTYLE_SECTIONS } from '../../utils/constants'
import '../style/DesktopLifestyle.css'
import DesktopSectionHeader from './DesktopSectionHeader'

const DesktopLifestyle = ({ setFacet, setSaving }) => {
  const { handleSubmit, handleValueChange, lifestyleData, setLifestyleData } = useLifestyle({
    setFacet,
    setSaving
  })

  // Unsaved changes modal state
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const initialState = useRef(null)

  // Capture initial state when component loads - with delay to ensure state is loaded
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!initialState.current && lifestyleData) {
        initialState.current = JSON.parse(JSON.stringify(lifestyleData))
        console.log('Initial lifestyle state captured:', initialState.current)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [lifestyleData])

  // Clear initial state when component unmounts
  useEffect(() => {
    return () => {
      initialState.current = null
    }
  }, [])

  // Scroll to top when component mounts
  useEffect(() => {
    setTimeout(() => {
      document.documentElement.scrollTop = 0
      document.body.scrollTop = 0
    }, 0)
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = () => {
    if (!initialState.current) return false

    const hasChanges = JSON.stringify(lifestyleData) !== JSON.stringify(initialState.current)
    console.log('Lifestyle change detection:', {
      hasChanges,
      initial: initialState.current,
      current: lifestyleData
    })

    return hasChanges
  }

  // Handle navigation with unsaved changes check
  const handleNavigation = (navigationFunction) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(() => navigationFunction)
      setShowUnsavedModal(true)
    } else {
      navigationFunction()
    }
  }

  // Update initial state after successful save
  const updateInitialStateAfterSave = () => {
    initialState.current = JSON.parse(JSON.stringify(lifestyleData))
    console.log('Initial state updated after save:', initialState.current)
  }

  // Wrapper for handleSubmit that also updates initial state
  const handleSaveWithStateUpdate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle saving and then navigating
  const handleSaveAndNavigate = async () => {
    try {
      await handleSubmit()
      updateInitialStateAfterSave()
      setShowUnsavedModal(false)
      if (pendingNavigation) {
        pendingNavigation()
        setPendingNavigation(null)
      }
    } catch (error) {
      // Error is already handled in handleSubmit
    }
  }

  // Handle navigation without saving
  const handleNavigateWithoutSaving = () => {
    setShowUnsavedModal(false)
    if (pendingNavigation) {
      pendingNavigation()
      setPendingNavigation(null)
    }
  }

  const renderFormItem = (key, template, section) => {
    switch (template.type) {
      case 'switch':
        return (
          <div className='desktop-lifestyle-switch'>
            <Switch
              checkedChildren={template.checkedChildren}
              unCheckedChildren={template.uncheckedChildren}
              checked={lifestyleData[key]}
              onChange={(value) => handleValueChange(key, value)}
              size='default'
            />
          </div>
        )
      case 'radioGroup':
        return (
          <Row gutter={[16, 16]} className='desktop-lifestyle-options'>
            {template.options.map((opt) => {
              const isChecked = lifestyleData[key] === opt.value

              return (
                <Col xs={24} sm={12} key={opt.value}>
                  <div
                    className={`desktop-lifestyle-option ${isChecked ? 'selected' : ''}`}
                    onClick={() => handleValueChange(key, opt.value)}
                    role='radio'
                    aria-checked={isChecked}
                    tabIndex={0}
                    style={{
                      '--section-color': section.color
                    }}
                  >
                    <div className='desktop-lifestyle-option-icon'>
                      <FontAwesomeIcon icon={section.icon} />
                    </div>
                    <span className='desktop-lifestyle-option-label'>{opt.label}</span>
                  </div>
                </Col>
              )
            })}
          </Row>
        )
      default:
        return null
    }
  }

  const renderSection = (section) => {
    const template = MedicalHistoryFormTemplates.dataModel[section.key]

    if (template.dependency) {
      // eslint-disable-next-line
      const [_, dependencyField] = template.dependency
      if (!lifestyleData[dependencyField]) return null
    }

    const dependentFields = Object.entries(MedicalHistoryFormTemplates.dataModel).filter(
      ([_, fieldTemplate]) => fieldTemplate.dependency && fieldTemplate.dependency[1] === section.key
    )

    return (
      <Card key={section.key} className='desktop-lifestyle-section-card'>
        <div className='desktop-lifestyle-section-header'>
          <div className='desktop-lifestyle-section-icon' style={{ '--section-color': section.color }}>
            <FontAwesomeIcon icon={section.icon} />
          </div>
          <div className='desktop-lifestyle-section-title'>
            <h3>{section.title}</h3>
            <p className='desktop-lifestyle-section-subtitle'>{section.subtitle}</p>
          </div>
        </div>

        <div className='desktop-lifestyle-section-content'>
          {renderFormItem(section.key, template, section)}

          {/* Render dependent fields only if the main switch is true */}
          {lifestyleData[section.key] &&
            dependentFields.map(([dependentKey, dependentTemplate]) => (
              <div key={dependentKey} className='desktop-lifestyle-dependent-field'>
                <h4 className='desktop-lifestyle-dependent-title'>{dependentTemplate.label}</h4>
                {renderFormItem(dependentKey, dependentTemplate, section)}
              </div>
            ))}
        </div>
      </Card>
    )
  }

  return (
    <div className='desktop-lifestyle-container'>
      <DesktopSectionHeader
        icon={faPersonRunning}
        title='Lifestyle'
        subtitle='Tell us about your daily habits and lifestyle'
        gradient='linear-gradient(135deg, #D81B60 0%, #EC407A 100%)'
      />

      <div className='desktop-lifestyle-content'>
        <Row gutter={[24, 24]}>
          {LIFESTYLE_SECTIONS.map((section) => (
            <Col xs={24} lg={12} key={section.key}>
              {renderSection(section)}
            </Col>
          ))}
        </Row>
      </div>

      <DesktopMedicalHistoryNav
        prevFunction={() =>
          handleNavigation(() => {
            setLifestyleData({
              diet: '',
              physicalActivity: '',
              sleepHours: '',
              waterIntake: '',
              screenTime: '',
              stress: '',
              alcoholConsumption: false,
              alcoholConsumptionLevel: '',
              smoking: false,
              smokingLevel: '',
              smokingYears: '',
              vaping: false,
              vapingLevel: '',
              vapingYears: ''
            })
            setFacet(null)
          })
        }
        nextFunction={handleSaveWithStateUpdate}
        prevText='Back to Overview'
        nextText='Save Lifestyle'
      />

      {/* Unsaved Changes Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <FontAwesomeIcon icon={faPersonRunning} style={{ color: '#ff6b6b' }} />
            <span>Unsaved Changes</span>
          </div>
        }
        open={showUnsavedModal}
        onCancel={() => setShowUnsavedModal(false)}
        footer={null}
        className='desktop-unsaved-changes-modal'
        centered
      >
        <div className='desktop-unsaved-changes-content'>
          <Typography.Text className='desktop-unsaved-changes-message'>
            You have unsaved changes to your lifestyle information. What would you like to do?
          </Typography.Text>

          <div className='desktop-unsaved-changes-actions'>
            <Button type='primary' className='desktop-save-changes-btn' onClick={handleSaveAndNavigate} loading={false}>
              Save Changes
            </Button>
            <Button type='default' className='desktop-discard-changes-btn' onClick={handleNavigateWithoutSaving}>
              Discard Changes
            </Button>
            <Button type='text' className='desktop-cancel-modal-btn' onClick={() => setShowUnsavedModal(false)}>
              Continue Editing
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default DesktopLifestyle
